<template>
  <div>
    <b-form @keydown.enter.prevent @submit.prevent="submitForm">
      <b-row class="mb-3 align-items-center">
        <b-col cols="4" class="text-right">Listing Exchange</b-col>
        <b-col cols="8">
          <b-form-select :options="listingExchangeOptions" v-model="listingExchange" required />
        </b-col>
      </b-row>
      <b-row class="mb-3 align-items-center">
        <b-col cols="4" class="text-right">Market cap range (M$)</b-col>
        <b-col cols="4">
          <b-form-input type="number" v-model="marketCapMin" placeholder="min" required min="1" max="1e9" step="1" autocomplete="off" />
        </b-col>
        <b-col cols="4">
          <b-form-input type="number" v-model="marketCapMax" placeholder="max" required min="1" max="1e9" step="1" autocomplete="off" />
        </b-col>
      </b-row>
      <b-row class="mb-3 align-items-center">
        <b-col cols="4" class="text-right">Trade Account</b-col>
        <b-col cols="8">
          <v-select class="vs-normalizer-2 d-inline-block" style="width: 300px;"
                    :options="cexAccounts"
                    :filterBy="cexAccountSelectFilterBy"
                    label="label"
                    v-model="tradeCexAccountId"
                    :reduce="acc => acc.id"
                    :clearable="false">
            <template #selected-option="acc">
              <img style="width: 1rem; height: 1rem; object-fit: contain;" class="mr-2" :src="exchangeLogos[acc.exchange]" :alt="acc.exchange">
              <span>{{ acc.label }}</span>
            </template>
            <template #option="acc">
              <img style="width: 1rem; height: 1rem; object-fit: contain;" class="mr-2" :src="exchangeLogos[acc.exchange]" :alt="acc.exchange">
              <span>{{ acc.label }}</span>
            </template>
          </v-select>
        </b-col>
      </b-row>
      <b-row class="mb-3 align-items-center">
        <b-col cols="4" class="text-right">Market type</b-col>
        <b-col cols="8">
          <b-form-radio-group :options="tradeMarketTypeOptions" v-model="tradeMarketType" required />
        </b-col>
      </b-row>
      <b-row class="mb-3 align-items-center">
        <b-col cols="4" class="text-right">Notional volume</b-col>
        <b-col cols="8">
          <b-input-group>
            <b-form-input class="text-right" type="number" required v-model="notional" min="10" autocomplete="off"></b-form-input>
            <b-input-group-append is-text>USDT</b-input-group-append>
          </b-input-group>
        </b-col>
      </b-row>
      <b-row class="mb-3 align-items-center">
        <b-col cols="4" class="text-right">Max price impact</b-col>
        <b-col cols="8">
          <b-input-group>
            <b-form-input class="text-right" type="number" required v-model="maxImpactPercent" min="-50" max="500" step="1" autocomplete="off"></b-form-input>
            <b-input-group-append is-text>%</b-input-group-append>
          </b-input-group>
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col cols="4" class="text-right">Time in force</b-col>
        <b-col cols="8">
          <b-form-radio-group :options="timeInForceOptions" v-model="timeInForce"></b-form-radio-group>
        </b-col>
      </b-row>
      <template v-if="['ioc', 'fok'].includes(timeInForce)">
        <b-row class="mb-3 align-items-center">
          <b-col cols="4" class="text-right">
            <b-form-checkbox v-model="enableTakeProfit">Take profit</b-form-checkbox>
          </b-col>
          <b-col cols="8" v-if="enableTakeProfit">
            <b-input-group>
              <b-form-input class="text-right" type="number" required v-model="takeProfitPercent" min="1" autocomplete="off"></b-form-input>
              <b-input-group-append is-text>%</b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>
        <b-row class="mb-3 align-items-center">
          <b-col cols="4" class="text-right">
            <b-form-checkbox v-model="enableStopLoss">Stop loss</b-form-checkbox>
          </b-col>
          <b-col cols="8" v-if="enableStopLoss">
            <b-input-group>
              <b-form-input class="text-right" type="number" required v-model="stopLossPercent" min="1"  autocomplete="off"></b-form-input>
              <b-input-group-append is-text>%</b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>
      </template>
      <div v-if="formErrorMsg" class="text-danger text-center">{{ formErrorMsg }}</div>
      <div class="text-center mt-4">
        <b-button type="submit" variant="primary" @click="beforeSubmit" :disabled="isSubmitting">Place order</b-button>
      </div>
    </b-form>
  </div>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import _ from "lodash";
  import * as cexAccountService from "@/services/cexAccountService";
  import * as listingTradeSettingsService from "@/services/listingTradeSettingsService";
  import * as utils from "@/utils";
  import * as constants from "@/constants";
  import BigNumber from "bignumber.js";

  export default {
    name: "AddListingTradeSettingsModal",
    inject: ["toastError", "toastSuccess", "toastSuccessDelay", "showLoading", "hideLoading"],
    props: ["modalId", "tradeSettings"],
    emits: ["done"],

    data() {
      return {
        exchangeLogos: constants.EXCHANGE_LOGO,

        listingExchangeOptions: [
          { value: "upbit", text: "Upbit KRW" },
        ],
        listingExchange: "upbit",

        marketCapMin: "",
        marketCapMax: "",

        notional: "",
        maxImpactPercent: 15,

        timeInForceOptions: [
          { value: "ioc", text: "IOC" },
          { value: "fok", text: "FOK" },
          { value: "gtc", text: "GTC" },
        ],
        timeInForce: "ioc",

        enableTakeProfit: false,
        takeProfitPercent: "",

        enableStopLoss: false,
        stopLossPercent: "",

        cexAccounts: [],
        tradeCexAccountId: 0,

        tradeMarketTypeOptions: [
          { value: "futures", text: "USDT Perpetual" },
          { value: "spot", text: "Spot" },
        ],
        tradeMarketType: "futures",

        formErrorMsg: "",
        isSubmitting: false,
      };
    },

    mounted() {
      this.setup();
    },

    methods: {
      async setup() {
        const existingTradeSettings = this.tradeSettings;
        if (existingTradeSettings) {
          this.listingExchange = existingTradeSettings.listingExchange;
          this.tradeCexAccountId = existingTradeSettings.tradeCexAccountId;
          this.marketCapMin = BigNumber(existingTradeSettings.marketCapMin).shiftedBy(-6).toFixed(0);
          this.marketCapMax = BigNumber(existingTradeSettings.marketCapMax).shiftedBy(-6).toFixed(0);
          this.notional = existingTradeSettings.notional;
          this.timeInForce = existingTradeSettings.timeInForce;
          this.maxImpactPercent = existingTradeSettings.maxImpactPercent;
          this.enableTakeProfit = !!existingTradeSettings.takeProfitPercent;
          this.takeProfitPercent = existingTradeSettings.takeProfitPercent;
          this.enableStopLoss = !!existingTradeSettings.stopLossPercent;
          this.stopLossPercent = existingTradeSettings.stopLossPercent;
        } else {
          const lastUsedAccountIdByExchange = JSON.parse(localStorage.getItem("cexLastUsedAccountIdByExchange"));
          this.tradeCexAccountId = lastUsedAccountIdByExchange.binance || lastUsedAccountIdByExchange.bybit;
        }

        this.cexAccounts = (await cexAccountService.getAll()).filter(a => ["binance", "bybit"].includes(a.exchange));
      },

      cexAccountSelectFilterBy(option, label, search) {
        return utils.searchText(label, search);
      },

      beforeSubmit() {

      },

      async submitForm() {
        try {
          this.isSubmitting = true;

          const tradeCexAccount = this.cexAccounts.find(a => a.id === this.tradeCexAccountId);
          let tradeExchange = tradeCexAccount.exchange;
          if (this.tradeMarketType === "futures") {
            tradeExchange += "-futures";
          }

          const id = this.tradeSettings?.id;
          const funcName = id ? "edit" : "add";
          await listingTradeSettingsService[funcName]({
            id,
            listingExchange: this.listingExchange,
            marketCapMin: BigNumber(this.marketCapMin).shiftedBy(6).toNumber(),
            marketCapMax: BigNumber(this.marketCapMax).shiftedBy(6).toNumber(),
            tradeCexAccountId: this.tradeCexAccountId,
            tradeExchange,
            notional: +this.notional,
            timeInForce: this.timeInForce,
            maxImpactPercent: +this.maxImpactPercent,
            takeProfitPercent: this.enableTakeProfit? +this.takeProfitPercent : null,
            stopLossPercent: this.enableStopLoss? +this.stopLossPercent : null,
            stopLossType: this.enableStopLoss? "market" : null,
          });

          this.$bvModal.hide(this.modalId);
          this.done();
          this.toastSuccessDelay("Order placed");

        } catch (e) {
          console.error(e);
          this.formErrorMsg = e.response?.data?.message || e.message;

        } finally {
          this.isSubmitting = false;
        }
      },

      done() {
        this.$emit("done");
      }
    }
  };
</script>
